import { useCallback, useMemo } from 'react';
import { Alert, Space, Typography } from 'antd';
import {
  useSiteCoordinatePickerMap,
  type SiteCoordinateSelection,
} from '~/app/hooks/site/useSiteCoordinatePickerMap';

const { Paragraph, Text } = Typography;

type Props = {
  address: string;
  cityName?: string;
  provinceName?: string;
  latitude?: string;
  longitude?: string;
  selectionRequiredText: string;
  instructionsText: string;
  currentCoordinatesText: string;
  missingKeyText: string;
  geocodeFailedText: string;
  loadFailedText: string;
  locatingText: string;
  onCoordinatesChange: (coordinates: {
    latitude: string;
    longitude: string;
  }) => void;
  onLocationSelect?: (
    selection: SiteCoordinateSelection,
  ) => Promise<void> | void;
};

function toNullableNumber(value?: string): number | null {
  if (!value?.trim()) {
    return null;
  }

  const numericValue = Number(value);

  return Number.isFinite(numericValue) ? numericValue : null;
}

export function SiteCoordinatePicker({
  address,
  cityName,
  provinceName,
  latitude,
  longitude,
  selectionRequiredText,
  instructionsText,
  currentCoordinatesText,
  missingKeyText,
  geocodeFailedText,
  loadFailedText,
  locatingText,
  onCoordinatesChange,
  onLocationSelect,
}: Props) {
  const selectedCoordinates = useMemo(() => {
    const latitudeNumber = toNullableNumber(latitude);
    const longitudeNumber = toNullableNumber(longitude);

    if (latitudeNumber === null || longitudeNumber === null) {
      return null;
    }

    return {
      latitude: latitudeNumber,
      longitude: longitudeNumber,
    };
  }, [latitude, longitude]);

  const isDisabled = !cityName || !provinceName;

  const addressQuery = useMemo(() => {
    if (isDisabled) {
      return null;
    }

    return [address.trim(), cityName, provinceName, 'France']
      .filter(Boolean)
      .join(', ');
  }, [address, cityName, isDisabled, provinceName]);

  const handleCoordinatesSelect = useCallback(
    (coordinates: { latitude: number; longitude: number }) => {
      onCoordinatesChange({
        latitude: coordinates.latitude.toFixed(6),
        longitude: coordinates.longitude.toFixed(6),
      });
    },
    [onCoordinatesChange],
  );

  const { errorMessage, isLocating, mapContainerRef } =
    useSiteCoordinatePickerMap({
      addressQuery,
      selectedCoordinates,
      disabled: isDisabled,
      missingKeyText,
      geocodeFailedText,
      loadFailedText,
      onCoordinatesSelect: handleCoordinatesSelect,
      onLocationSelect,
    });

  return (
    <Space direction="vertical" size={12} style={{ width: '100%' }}>
      <div>
        <Paragraph type="secondary" style={{ marginBottom: 4 }}>
          {instructionsText}
        </Paragraph>
        {selectedCoordinates ? (
          <Text type="secondary">
            {currentCoordinatesText}: {selectedCoordinates.latitude.toFixed(6)},{' '}
            {selectedCoordinates.longitude.toFixed(6)}
          </Text>
        ) : null}
      </div>

      {isDisabled ? (
        <Alert type="info" showIcon message={selectionRequiredText} />
      ) : errorMessage ? (
        <Alert type="warning" showIcon message={errorMessage} />
      ) : null}

      <div
        ref={mapContainerRef}
        style={{
          width: '100%',
          minHeight: 280,
          borderRadius: 12,
          overflow: 'hidden',
          background: '#f8fafc',
          border: '1px solid #d9e4f0',
          display: isDisabled ? 'none' : 'block',
        }}
      />

      {!isDisabled && isLocating ? (
        <Text type="secondary">{locatingText}</Text>
      ) : null}
    </Space>
  );
}
